import { useEffect, useState } from 'react';
import { Browser } from '@capacitor/browser';
import { ExternalLink, Loader2, Receipt } from 'lucide-react';
import { paymentsAPI } from '../api/client';
import { isNativePlatform } from '../native';
import type { MemberReceipt } from '../types';

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function label(receipt: MemberReceipt) {
  if (receipt.description) return receipt.description;
  if (receipt.kind === 'monthly_dues') {
    return `${new Date(receipt.date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} dues`;
  }
  return 'One-time charge';
}

export default function BillingReceipts() {
  const [receipts, setReceipts] = useState<MemberReceipt[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    paymentsAPI
      .getReceipts()
      .then((data) => {
        setReceipts(data.receipts);
        setFailed(data.incomplete && data.receipts.length === 0);
      })
      .catch(() => {
        setReceipts([]);
        setFailed(true);
      });
  }, []);

  const open = (url: string) => {
    if (isNativePlatform) {
      void Browser.open({ url });
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 px-4 py-3 space-y-3">
      <p className="text-sm font-semibold text-navy flex items-center gap-1.5">
        <Receipt size={15} />
        Receipts
      </p>
      {receipts === null ? (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 size={16} className="animate-spin" />
          Loading…
        </div>
      ) : failed ? (
        <p className="text-sm text-gray-500">
          Couldn't load your receipts right now. Please try again later.
        </p>
      ) : receipts.length === 0 ? (
        <p className="text-sm text-gray-500">No payments yet.</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {receipts.map((receipt) => (
            <li key={receipt.id} className="flex items-center justify-between gap-3 py-2.5">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-800 break-words">{label(receipt)}</p>
                <p className="text-xs text-gray-500">
                  {formatDate(receipt.date)} · ${receipt.amount}
                </p>
              </div>
              <button
                type="button"
                onClick={() => open(receipt.receipt_url)}
                className="flex items-center gap-1.5 shrink-0 px-3 py-1.5 border border-navy text-navy text-sm font-semibold rounded-lg hover:bg-off-white transition"
              >
                <ExternalLink size={14} />
                Receipt
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
